import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Grid from "@mui/material/Grid";
import { alpha, useTheme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { parseFloatToFixed } from "../../extras";
import { dateTimeMask } from "../../common/dateMask";
import DashboardNoShippingRouteAlert from "../dashboard/DashboardNoShippingRouteAlert";
import { formatFinance } from "../../utils/formatters";

// Animation variants
const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.02,
    backgroundColor: "#f5f5f5",
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

const ShippingRoutesList = ({
  isDashboard = false,
  shippingRoutes,
  onShowMore,
  onViewRoute,
  onEditDriver,
  onDelivery,
  onAddNewOrder,
  showMore = true,
}) => {
  const theme = useTheme();

  if (shippingRoutes.length === 0 && isDashboard) {
    return <DashboardNoShippingRouteAlert />;
  }

  return (
    <Box sx={{ position: "relative", p: 1 }}>
      <AnimatePresence>
        <Grid
          container
          spacing={2}
          component={motion.div}
          variants={listVariants}
          initial="hidden"
          animate="show"
        >
          {shippingRoutes.map((shippingRoute) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              xl={4}
              key={shippingRoute.id}
              component={motion.div}
              variants={itemVariants}
              layout
            >
              <Card
                variant="outlined"
                onClick={() => onViewRoute(shippingRoute.id)}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  borderColor: alpha(theme.palette.grey[300], 0.8),
                  backgroundColor: theme.palette.background.paper,
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.common.black,
                      0.08
                    )}`,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2, pb: 1 }}>
                  {/* Header: Driver & Time */}
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: 40,
                          height: 40,
                        }}
                      >
                        <SportsMotorsportsIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          lineHeight={1.2}
                        >
                          {shippingRoute.driver?.easy_id}
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={500}
                          >
                            {shippingRoute.driver?.name}
                          </Typography>
                          {onEditDriver && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditDriver(shippingRoute.id);
                              }}
                              sx={{
                                p: 0.25,
                                ml: 0.5,
                                color: theme.palette.text.secondary,
                                "&:hover": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                      label={dateTimeMask(shippingRoute.dispatched_at)} // Showing only time for compactness, or full date if needed
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        borderColor: alpha(theme.palette.grey[400], 0.5),
                        "& .MuiChip-icon": { fontSize: 12 },
                      }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5, opacity: 0.6 }} />

                  {/* Body: Orders */}
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{
                          mb: 0.5,
                          display: "block",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Pedidos
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {shippingRoute.shipping_route_orders.map((order, i) => (
                          <Chip
                            key={order.fk_order.order_index}
                            label={`#${order.fk_order.order_index}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.75rem",
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08
                              ),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              borderRadius: 1,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Additional Info: Equipment, Change, Total */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {shippingRoute.equipment && (
                        <Chip
                          icon={<SettingsRemoteIcon />}
                          label={shippingRoute.equipment.name}
                          size="small"
                          sx={{
                            height: 24,
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.dark,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            "& .MuiChip-icon": {
                              color: theme.palette.info.dark,
                              fontSize: 16,
                            },
                          }}
                        />
                      )}

                      {shippingRoute.change &&
                        parseFloat(shippingRoute.change) > 0 && (
                          <Chip
                            icon={<MonetizationOnOutlinedIcon />}
                            label={formatFinance(
                              parseFloatToFixed(shippingRoute.change)
                            )}
                            size="small"
                            sx={{
                              height: 24,
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                              color: theme.palette.warning.dark,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              "& .MuiChip-icon": {
                                color: theme.palette.warning.dark,
                                fontSize: 16,
                              },
                            }}
                          />
                        )}

                      {!isDashboard && (
                        <Chip
                          icon={<LocalShippingIcon />}
                          label={formatFinance(
                            shippingRoute.total_delivery_tax / 100
                          )}
                          size="small"
                          sx={{
                            height: 24,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.dark,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            "& .MuiChip-icon": {
                              color: theme.palette.success.dark,
                              fontSize: 16,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Stack>
                </CardContent>

                <Divider sx={{ opacity: 0.6 }} />

                <CardActions
                  sx={{ p: 1.5, pt: 1, justifyContent: "flex-end", gap: 1 }}
                >
                  <Tooltip title="Ver Detalhes">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewRoute(shippingRoute.id);
                      }}
                      sx={{
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 2,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {onAddNewOrder && (
                    <Tooltip title="Adicionar Pedido">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddNewOrder(shippingRoute.id);
                        }}
                        sx={{
                          border: `1px solid ${theme.palette.primary.main}`,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: 2,
                          color: theme.palette.primary.main,
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {onDelivery && (
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      startIcon={<CheckIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelivery(shippingRoute.id, shippingRoute.equipment);
                      }}
                      sx={{
                        ml: "auto !important",
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        bgcolor: theme.palette.success.main,
                        "&:hover": {
                          bgcolor: theme.palette.success.dark,
                        },
                      }}
                    >
                      Retornou
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </AnimatePresence>

      {!isDashboard && showMore && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ marginTop: 24 }}
        >
          <Button
            component={motion.div}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            size="large"
            fullWidth
            startIcon={<ExpandMoreIcon />}
            onClick={onShowMore}
            sx={{
              py: 1.5,
              borderRadius: 3,
              color: theme.palette.text.secondary,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.grey[300]}`,
              textTransform: "none",
              fontSize: "1rem",
              "&:hover": {
                bgcolor: theme.palette.grey[50],
                borderColor: theme.palette.grey[400],
              },
            }}
          >
            {shippingRoutes.length > 0
              ? "Mostrar mais rotas"
              : "Nenhuma Entrega Encontrada"}
          </Button>
        </motion.div>
      )}
    </Box>
  );
};

export default ShippingRoutesList;
